<template>
  <IonPage color="white">
    <MapModalHeader>
      <PropertyToolbar></PropertyToolbar>
    </MapModalHeader>
    <IonContent color="white" class="bg-white">
      <IonRefresher :mode="refresherMode" slot="fixed" @ion-refresh="IonRefresh($event)">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div class="p-6">
        <div class="flex gap-4">
          <div class="w-3/5 space-y-6">
            <BaseLabeledField :loading="propertyStore.isLoading" animate-loading>
              <template #label>billing</template>
              <template #field>{{ propertyStore.property?.billingCompany?.name }}</template>
            </BaseLabeledField>
            <BaseLabeledField :loading="propertyStore.isLoading" animate-loading>
              <template #label>management</template>
              <template #field>{{ propertyStore.property?.managementCompany?.name }}</template>
            </BaseLabeledField>
          </div>
          <PropertyContactInfo class="w-2/5"></PropertyContactInfo>
        </div>

        <div class="my-8 rounded-md bg-gray-100 text-xs text-gray-500 flex justify-between items-center px-1">
          <CommentsButton
            :on-refresh="IonRefresh"
            :comments="propertyStore.property?.comments"
            :on-remove-comment="propertyStore.removeComment"
            :on-add-comment="propertyStore.addComment"
            title="Property Comments"
          ></CommentsButton>
          <PhotosButton
            :on-refresh="IonRefresh"
            :photos="photos"
            :on-remove-photo="propertyStore.removeDocument"
            :on-add-photo="propertyStore.addPhoto"
            title="Property Photos"
          ></PhotosButton>
          <DocsButton
            :on-refresh="IonRefresh"
            :docs="docs"
            :on-add-doc="propertyStore.addDocument"
            :on-remove-doc="propertyStore.removeDocument"
          ></DocsButton>
        </div>
        <GatewaySection class="mt-16" />
        <RepeatersSection class="mt-12" />
        <EquipmentSection class="mt-20" />
        <SectionTitle class="mt-20">Quick Actions</SectionTitle>
        <QuickActionButton
          @click="testConnectClicked"
          icon="icon-radio"
          title="Test Connect"
          subtitle="Quickly check a device's connectivity"
        />
        <QuickActionButton
          @click="replaceDeviceClicked"
          icon="icon-repeat"
          title="Replace a Device"
          subtitle="Scan a device barcode to simply replace"
        />
        <MeterConfigurationsModal></MeterConfigurationsModal>
        <QuickActionButton
          @click="deletePropertyClicked"
          icon="icon-trash-2"
          title="Delete Property"
          color="red"
          subtitle="Permanently delete this property and all its units."
        ></QuickActionButton>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { useSelectedProperty } from '@/stores/selected-property.store';
import {
  alertController,
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  loadingController,
  useIonRouter,
} from '@ionic/vue';
import PropertyContactInfo from './PropertyContactInfo.vue';
import { usePropertyDocs, usePropertyPhotos } from '@/composables/use-property-utils';
import GatewaySection from './sections/GatewaySection.vue';
import RepeatersSection from './sections/RepeatersSection.vue';
import EquipmentSection from './sections/EquipmentSection.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import QuickActionButton from '@/components/forms/QuickActionButton.vue';
import BaseLabeledField from '@/components/BaseLabeledField.vue';
import CommentsButton from '@/components/comments/CommentsButton.vue';
import PhotosButton from '@/components/photos/PhotosButton.vue';
import DocsButton from '@/components/docs/DocsButton.vue';
import { useToast } from '@/composables/use-toast';
import { useUser } from '@/stores/user.store';
import { SearchRow } from 'common';
import MeterConfigurationsModal from './property-meter-configurations/MeterConfigurationsModal.vue';
import { useRefresherMode } from '@/composables/use-refresher-mode';
import PropertyToolbar from '../toolbars/PropertyToolbar.vue';
import MapModalHeader from '../MapModalHeader.vue';
import { useNewMap } from '@/stores/map/map.store';

const propertyStore = useSelectedProperty();

const { photos } = usePropertyPhotos(() => propertyStore.property);
const { docs } = usePropertyDocs(() => propertyStore.property);
const { successToast, errorToast } = useToast();
const router = useIonRouter();
const map = useNewMap();
const user = useUser();
const { mode: refresherMode } = useRefresherMode();

async function IonRefresh(e?: CustomEvent) {
  await propertyStore.refresh();
  (e?.target as any)?.complete();
}

function testConnectClicked() {
  if (propertyStore.property) {
    router.push({
      path: '/test-connect',
      query: { property: JSON.stringify(SearchRow.FromProperty(propertyStore.property)) },
    });
  }
}

function replaceDeviceClicked() {
  router.push('/replace-device');
}

async function deletePropertyClicked() {
  if (propertyStore.property) {
    const id = propertyStore.property._id;
    const a = await alertController.create({
      header: 'Are you sure?',
      message: `Are you sure you want to delete ${propertyStore.property.name} and all its equipment? To confirm, type "confirm delete" below.`,
      inputs: [
        {
          type: 'text',
          placeholder: `confirm delete`,
        },
      ],
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel',
        },
        {
          role: 'destructive',
          text: 'Delete Property',
          handler: (values) => {
            return !!values[0] && typeof values[0] === 'string' && values[0].toLowerCase() === 'confirm delete';
          },
        },
      ],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (
      role === 'destructive' &&
      typeof data?.values?.[0] === 'string' &&
      data?.values?.[0]?.toLowerCase() === 'confirm delete'
    ) {
      const l = await loadingController.create({ message: 'Deleting property...' });
      await l.present();
      const { success, error } = await propertyStore.deleteProperty();
      await l.dismiss();
      if (success) {
        successToast('Property deleted!');
        map.onPropertyDeleted(id);
        user.recentlyViewedProperties = user.recentlyViewedProperties.filter((p) => p._id !== id);
        router.replace('/');
      } else if (error) {
        errorToast(error);
      }
    }
  }
}
</script>

<style scoped>
/* For some reason Android needs this  */
ion-refresher {
  z-index: 10;
}
</style>
